import {Component, OnInit, ChangeDetectionStrategy, NgZone, OnDestroy} from '@angular/core';
import {
  StracliCmdService,
  StracliCommandType,
  StracliPongDto,
  StracliCmdResponsePersistenceEntity
} from './stracli-cmd.service';
import {ToastrService} from 'ngx-toastr';
import {FormsModule} from '@angular/forms';
import {BaseurlService} from '../../services/baseurl.service';
import {DatePipe} from '@angular/common';

const COMMAND_EXECUTED_SUCCESSFULLY = 'Command executed successfully';
const COMMAND_EXECUTION_FAILED = 'Failed to execute command';

@Component({
  selector: 'app-stracli-cmd',
  imports: [
    FormsModule,
    DatePipe
  ],
  templateUrl: './stracli-cmd.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './stracli-cmd.component.scss'
})
export class StracliCmdComponent implements OnInit, OnDestroy {
  readonly StracliCommandType = StracliCommandType;

  hosts: Array<StracliPongDto>;
  text: string;
  responses: StracliCmdResponsePersistenceEntity[] = [];
  sse: EventSource;
  expandedRows: Set<number> = new Set();

  customActions: CustomAction[] = [
    {command: 'screencapture -x -p', type: StracliCommandType.BASH, host: 'nodens', description: 'Screenshot Nodens'},
  ];

  constructor(
    private stracliCmdService: StracliCmdService,
    private notificationService: ToastrService,
    private zone: NgZone,
    private baseUrlService: BaseurlService,
  ) { }

  ngOnInit(): void {
    this.stracliCmdService.getHosts().subscribe({
      next: res => this.hosts = res,
      error: err => this.notificationService.error('failed to retrieve data ' + JSON.stringify(err))
    });
    this.loadResponses();
    this.setupSse();
  }

  ngOnDestroy() {
    if (this.sse) {
      this.sse.close();
    }
  }

  loadResponses() {
    this.stracliCmdService.getResponses().subscribe({
      next: res => this.responses = [...res].sort((a, b) => (b.id ?? 0) - (a.id ?? 0)),
      error: err => this.notificationService.error('failed to retrieve responses ' + JSON.stringify(err))
    });
  }

  setupSse() {
    this.sse = new EventSource(this.baseUrlService.getBaseUrl() + '/stracliCommands/sse');
    this.sse.onmessage = (evt) => {
      console.log('received sse data', evt.data);
      this.zone.run(() => {
        let id: unknown;
        try {
          const parsed = JSON.parse(evt.data);
          id = (parsed && typeof parsed === 'object') ? parsed.id : parsed;
        } catch (e) {
          id = evt.data;
        }

        const numericId = parseInt(String(id));
        if (!isNaN(numericId)) {
          this.stracliCmdService.getStracliCmdResponse(numericId).subscribe({
            next: res => {
              const index = this.responses.findIndex(it => it.id === res.id);
              if (index !== -1) {
                this.responses[index] = res;
              } else {
                this.responses.unshift(res);
              }
            }
          });
        } else {
          this.loadResponses();
        }
      });
    };
  }

  toggleExpand(id: number | null) {
    if (id === null) {
      return;
    }
    if (this.expandedRows.has(id)) {
      this.expandedRows.delete(id);
    } else {
      this.expandedRows.add(id);
    }
  }

  isExpanded(id: number | null): boolean {
    return id !== null && this.expandedRows.has(id);
  }

  getParsed(resp: StracliCmdResponsePersistenceEntity): any {
    try {
      return JSON.parse(resp.response.typeSpecificResponse);
    } catch (e) {
      return null;
    }
  }

  executeDummy(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.DUMMY, {}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeShutdown(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.SHUTDOWN, {}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeBash(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.BASH, {text: this.text}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeSpel(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.SPEL, {text: this.text}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeCmd(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.CMD, {text: this.text}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executePowershell(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.POWERSHELL, {text: this.text}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeQwen(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.QWEN, {text: this.text}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeStartScreenshotStream(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.START_SCREENSHOT_STREAM, {}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeEndScreenshotStream(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.END_SCREENSHOT_STREAM, {}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeAi(host: string) {
    this.stracliCmdService.runCommand(host, StracliCommandType.AI, {text: this.text}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }

  executeCustomAction(customAction: CustomAction) {
    this.stracliCmdService.runCommand(customAction.host, customAction.type, {text: customAction.command}).subscribe({
      next: res => {
        this.notificationService.success(COMMAND_EXECUTED_SUCCESSFULLY);
      },
      error: err => {
        this.notificationService.error(`${COMMAND_EXECUTION_FAILED} ${JSON.stringify(err)}`);
      }
    });
  }
}

interface CustomAction {
  command: string;
  type: StracliCommandType;
  host: string;
  description: string;
}
